import { z } from 'zod';
/**
 * フィールドの Zod スキーマインスタンス → BQ メタ の対応表。`.nullable()` / `.optional()` は
 * 新しいスキーマインスタンスを生成するため、`.unwrap()` で base まで辿ってから引く。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business bq* コンストラクタが生成した Zod スキーマに BQ 型メタを WeakMap で紐付ける registry（Zod version 非依存）
 * @graph-connects none
 */
const fieldMetaRegistry = new WeakMap();
/**
 * Zod スキーマに BQ メタを登録して返す内部ヘルパ。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business bq* コンストラクタ共通の registry 登録処理
 * @graph-connects none
 */
function register(schema, meta) {
    fieldMetaRegistry.set(schema, meta);
    return schema;
}
/**
 * STRING カラムの契約フィールド。NULLABLE にする場合は呼び出し側で `.nullable()` を付与する。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ STRING カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqString(description) {
    return register(z.string(), { bqType: 'STRING', description, repeated: false });
}
/**
 * INT64 カラムの契約フィールド。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ INT64 カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqInt(description) {
    return register(z.number().int(), { bqType: 'INT64', description, repeated: false });
}
/**
 * FLOAT64 カラムの契約フィールド。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ FLOAT64 カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqFloat(description) {
    return register(z.number(), { bqType: 'FLOAT64', description, repeated: false });
}
/**
 * BOOL カラムの契約フィールド。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ BOOL カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqBool(description) {
    return register(z.boolean(), { bqType: 'BOOL', description, repeated: false });
}
/**
 * TIMESTAMP カラムの契約フィールド。JS 値は ISO 8601 文字列で保持し、書込み時に
 * `toBqRecord` が `BigQueryTimestamp` へ変換する（生文字列を type=TIMESTAMP で渡さない gotcha 対策）。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ TIMESTAMP カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqTimestamp(description) {
    return register(z.string(), { bqType: 'TIMESTAMP', description, repeated: false });
}
/**
 * `ARRAY<FLOAT64>`（REPEATED FLOAT64）カラムの契約フィールド。embedding ベクトル等に使う。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ REPEATED FLOAT64 カラムを表す Zod 契約フィールドコンストラクタ
 * @graph-connects none
 */
export function bqFloatArray(description) {
    return register(z.array(z.number()), { bqType: 'FLOAT64', description, repeated: true });
}
/**
 * フィールドスキーマ（`.nullable()` / `.optional()` でラップされている可能性あり）から
 * BQ メタと mode を解決する。`.unwrap()` で registry 登録済みの base まで辿る。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business 契約フィールドの BQ 型・mode（REQUIRED/NULLABLE/REPEATED）を解決する内部ヘルパ
 * @graph-connects none
 */
export function resolveBqField(field) {
    let base = field;
    while (!fieldMetaRegistry.has(base)) {
        const unwrap = base.unwrap;
        if (typeof unwrap !== 'function')
            break;
        base = unwrap.call(base);
    }
    const meta = fieldMetaRegistry.get(base);
    if (!meta) {
        throw new Error('resolveBqField: field was not constructed via a bq* helper (no BQ metadata registered)');
    }
    const nullable = field.safeParse(null).success || field.safeParse(undefined).success;
    const mode = meta.repeated ? 'REPEATED' : nullable ? 'NULLABLE' : 'REQUIRED';
    return { meta, mode };
}
/**
 * ZodObject 契約の shape を `[name, fieldSchema]` の配列として列挙する内部ヘルパ。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business 契約 ZodObject のカラム列挙（順序は宣言順を保持）
 * @graph-connects none
 */
function shapeEntries(contract) {
    return Object.entries(contract.shape);
}
/**
 * Zod 契約（SoT）から `gcp.bigquery.Table.schema` に渡す BQ スキーマ JSON 配列を導出する。
 * カラム順は契約の宣言順を保持する。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business Zod 契約から BQ テーブルスキーマ（name/type/mode/description）を生成し、二重管理を根絶する
 * @graph-connects none
 */
export function zodToBqSchema(contract) {
    return shapeEntries(contract).map(([name, field]) => {
        const { meta, mode } = resolveBqField(field);
        return { name, type: meta.bqType, mode, description: meta.description };
    });
}
/**
 * Zod 契約から MERGE / INSERT の named パラメータ用の `types` マップを導出する。
 * REPEATED は要素型のタプル（`['FLOAT64']`）で表す。
 *
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business Zod 契約から BQ クエリパラメータの型ヒント（null 値の型解決に必須）を生成する
 * @graph-connects none
 */
export function zodToBqParamTypes(contract) {
    const types = {};
    for (const [name, field] of shapeEntries(contract)) {
        const { meta } = resolveBqField(field);
        types[name] = meta.repeated ? [meta.bqType] : meta.bqType;
    }
    return types;
}
/**
 * BigQuery の識別子として使える綴り。先頭は英字か `_` で、以降は英数字と `_` だけである。
 * @graph-connects none
 */
const BQ_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
/**
 * 列名 1 つをバッククォートで囲み、SQL へ置ける識別子にする。
 *
 * **予約語と同じ綴りの列は、囲まないと構文エラーになる。** GoogleSQL の予約語は 97 語あり
 * （`AT` / `FROM` / `TO` / `ORDER` / `RANGE` 等）、そのうち 1 語でも列名に選ばれていれば、
 * その列を並べた `INSERT` / `SELECT` は実行のたびに決定的に落ちる。型検査もテストも通り、
 * BigQuery へ投げた瞬間にだけ落ちるので、組む側で必ず囲む以外に防ぎ方が無い
 * （`spec_graph_operations` の列 `at` が本番の復元で `Unexpected keyword AT` を出した）。
 *
 * **綴りを検査してから囲む。** 囲めば何でも識別子になるわけではなく、バッククォートを含む
 * 名前を渡されるとそこで囲みが閉じて後ろが SQL として解釈される。呼び出し側は契約由来の
 * 列名しか渡さない想定だが、囲む関数がその前提を自分で確かめる。
 *
 * @param name 列名
 * @returns バッククォートで囲んだ識別子
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ の列名をバッククォートで囲み、予約語と同じ綴りの列でも SQL が組めるようにする
 * @graph-connects none
 */
export function bqIdentifier(name) {
    if (!BQ_IDENTIFIER.test(name)) {
        throw new Error(`bigquery: 識別子として使えない列名である: ${name}`);
    }
    return `\`${name}\``;
}
/**
 * 列名の配列を、囲んだ並び（`` `a`, `b` ``）にする。
 *
 * **列名の配列が SQL になる経路はこの 1 本に集める。** `columns.map((c) => c.name).join(', ')`
 * を呼び出し側ごとに書くと、囲む側と囲まない側が並び、囲み忘れた 1 箇所だけが本番で落ちる。
 *
 * @param names 列名の配列
 * @returns `, ` で繋いだ、囲んだ列の並び
 * @graph-stack cortex-core
 * @graph-domain Infrastructure
 * @graph-business BQ の列名の配列を、囲んだ列の並びとして 1 箇所で組む
 * @graph-connects bqIdentifier [calls] 列名 1 つの囲み
 */
export function bqColumnList(names) {
    return names.map((name) => bqIdentifier(name)).join(', ');
}
//# sourceMappingURL=zod-bq-schema.js.map